import { useState, useEffect } from 'react'
import { UserService } from '@duck/api-definitions'
import {
  Button,
  Checkbox,
  Input,
  MarkdownEditor,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  MultiPick,
  TimePicker,
} from '@duck/ui'
import { useTar } from '@/modules/i18n/useTar'
import { apis } from '@/modules/apiClients'
import { toast } from 'sonner'
import {
  type ReleaseNoteLabel,
  RELEASE_NOTE_LABELS,
  LABEL_ICONS,
} from './releaseNoteLabels'

export type { ReleaseNoteLabel }

export interface ReleaseNoteFormData {
  slug: string
  title: string
  body: string
  noteDate: Date | undefined
  noteTime: string
  labels: ReleaseNoteLabel[]
  active: boolean
}

const emptyForm: ReleaseNoteFormData = {
  slug: '',
  title: '',
  body: '',
  noteDate: new Date(),
  noteTime: new Date().toTimeString().slice(0, 5),
  labels: ['feature'],
  active: true,
}

function combineDateAndTime(date: Date | undefined, time: string): string {
  const d = date ? new Date(date) : new Date()
  const [hours, minutes] = time.split(':').map(Number)
  d.setHours(hours || 0, minutes || 0, 0, 0)
  return d.toISOString()
}

interface ReleaseNoteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'create' | 'edit'
  note?: UserService.ReleaseNote | null
  onSuccess: () => void | Promise<void>
}

export const ReleaseNoteDialog = ({
  open,
  onOpenChange,
  mode,
  note,
  onSuccess,
}: ReleaseNoteDialogProps) => {
  const { t } = useTar()
  const [form, setForm] = useState<ReleaseNoteFormData>({ ...emptyForm })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open && mode === 'edit' && note) {
      const d = new Date(note.noteDate)
      setForm({
        slug: note.slug ?? note.id,
        title: note.title,
        body: note.body,
        noteDate: d,
        noteTime: d.toTimeString().slice(0, 5),
        labels: (note.labels as ReleaseNoteLabel[]) ?? ['feature'],
        active: note.active ?? true,
      })
    } else if (open && mode === 'create') {
      setForm({ ...emptyForm })
    }
  }, [open, mode, note])

  const handleSubmit = async () => {
    if (!form.slug || !form.title || !form.body) {
      toast.error(t('releaseNotes.fillAllFields'))
      return
    }
    try {
      setSaving(true)
      if (mode === 'create') {
        await apis.userService.releaseNotesPost({
          slug: form.slug,
          title: form.title,
          body: form.body,
          noteDate: combineDateAndTime(form.noteDate, form.noteTime),
          labels: form.labels,
          active: form.active,
        })
        toast.success(t('releaseNotes.created'))
      } else if (note) {
        await apis.userService.releaseNotesIdPut(note.id, {
          slug: form.slug,
          title: form.title,
          body: form.body,
          noteDate: combineDateAndTime(form.noteDate, form.noteTime),
          labels: form.labels,
          active: form.active,
        })
        toast.success(t('releaseNotes.updated'))
      }
      await Promise.resolve(onSuccess())
      onOpenChange(false)
    } catch (error) {
      console.error(mode === 'create' ? 'Failed to create release note:' : 'Failed to update release note:', error)
      toast.error(mode === 'create' ? t('releaseNotes.createError') : t('releaseNotes.updateError'))
    } finally {
      setSaving(false)
    }
  }

  const title = mode === 'create' ? t('releaseNotes.addNew') : t('releaseNotes.editNote')
  const submitLabel = mode === 'create' ? t('releaseNotes.create') : t('releaseNotes.save')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent width="md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">{t('releaseNotes.slugLabel')}</label>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder={t('releaseNotes.slugPlaceholder')}
            />
          </div>
          <div>
            <label className="text-sm font-medium">{t('releaseNotes.titleLabel')}</label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={t('releaseNotes.titlePlaceholder')}
            />
          </div>
          <div>
            <label className="text-sm font-medium">{t('common.labels')}</label>
            <MultiPick
              options={RELEASE_NOTE_LABELS.map((label) => ({
                label: t(`releaseNotes.labels.${label}`),
                value: label,
                icon: LABEL_ICONS[label],
              }))}
              selected={form.labels}
              onChange={(vals) =>
                setForm({
                  ...form,
                  labels: vals.filter((v): v is ReleaseNoteLabel =>
                    RELEASE_NOTE_LABELS.includes(v as ReleaseNoteLabel)
                  ),
                })
              }
              placeholder={t('common.selectItems')}
            />
          </div>
          <div>
            <label className="text-sm font-medium">{t('releaseNotes.bodyLabel')}</label>
            <MarkdownEditor
              defaultValue={form.body}
              onChange={(value) => setForm({ ...form, body: value })}
              mode="edit"
              placeholder={t('releaseNotes.bodyPlaceholder')}
            />
          </div>
          <div>
            <label className="text-sm font-medium">{t('releaseNotes.noteDateLabel')}</label>
            <TimePicker
              time={form.noteTime}
              onTimeChange={(time) => setForm({ ...form, noteTime: time })}
              datePickerProps={{
                value: form.noteDate,
                onChange: (date) => setForm({ ...form, noteDate: date }),
              }}
              inputProps={{
                placeholder: t('common.selectTime'),
              }}
            />
          </div>
          {mode === 'edit' && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="release-note-active"
                checked={form.active}
                onCheckedChange={(checked) => setForm({ ...form, active: checked === true })}
              />
              <label htmlFor="release-note-active" className="text-sm font-medium cursor-pointer">
                {t('releaseNotes.active')}
              </label>
            </div>
          )}
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? t('common.saving') : submitLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
